import React, { useState } from "react";
import styled from "styled-components";
import { device, size } from "../../constants";
import { HighlightBar } from "./HighlightBar";
import { useWindowSize } from "../../hooks/useWindowResize";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { SlideBulletNavigation } from "../blog/RelatedPosts/SlideBulletNavigation";

const Section = styled.section`
	background: ${({ theme }) => theme.colors["background-02"]};
	text-align: center;
	
	.bar > div {
		margin: 0 auto;
	}

  .swiper {
    overflow: visible;
  }
`;

const Title = styled.h2`
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors["neutral-01"]};
  margin: 16px 0;

  @media ${device.laptopL} {
    font-size: 32px;
    line-height: 48px;
    margin-top: 24px;
	}
`;

const Subtitle = styled.p`
	font-weight: 400;
  font-size: 18px;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors["neutral-01"]};
  margin: 0 0 80px 0;

  @media ${device.laptopL} {
    font-size: 20px;
    line-height: 36px;
    max-width: 850px;
    margin: 0 auto 104px;
  }
`;

const CardsWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 40px;
`;

const Card = styled.div`
	background: ${({ theme }) => theme.colors["neutral-07"]};
	border-radius: 8px;
	box-shadow: 0px 12px 6px 0px rgba(0, 0, 0, 0.15), 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
	padding: 48px 16px 16px;
	position: relative;

	@media ${device.laptopL} {
		max-width: 400px;
    padding: 80px 24px 24px;
	}
`;

const CardNumber = styled.div`
	background: ${({ theme }) => theme.colors["neutral-06"]};
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.1), 0px 10px 15px 0px rgba(0, 0, 0, 0.1);
  font-weight: 500;
  font-size: 48px;
  line-height: 48px;
  letter-spacing: 0px;
  text-align: center;
  color: var(--product-color);

	position: absolute;
	top: 0;
	left: 50%;
	transform: translate(-50%, -50%);
	border-radius: 8px;
	width: 96px;
	height: 72px;
	display: flex;
	align-items: center;
	justify-content: center;

  @media ${device.laptopL} {
		width: 132px;
    height: 96px;
    font-size: 56px;
    line-height: 48px;
	}
`;

const CardTitle = styled.h3`
  font-weight: 500;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors["neutral-01"]};
  margin-bottom: 8px;

  @media ${device.laptopL} {
    font-size: 24px;
    line-height: 32px;
    margin-bottom: 16px;
  }
`;

const CardText = styled.p`
  font-weight: 400;
  font-size: 18px;
  line-height: 30px;
  letter-spacing: 0px;
  text-align: center;
  margin: 0;
  color: ${({ theme }) => theme.colors["neutral-03"]};

  @media ${device.laptopL} {
    font-size: 20px;
    line-height: 32px;
  }
`;

const SwiperWrapper = styled.div`
	margin-top: 40px;

  .bullets-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 24px;
  }

  .bullet {
    background-color: var(--product-color);
    opacity: 0.5;
  }

  .bullet-active {
    opacity: 1;
  }

  :is([data-theme="dark"] &) {
    .bullet {
      background-color: var(--product-color);
    }
  }
`;



export default function WhyChooseSection({ 
	title,
	description,
	cards,
	isMobile,
}) {
	const [activeBullet, setActiveBullet] = useState(0);

	return (
		<Section>
			<div className="container-blog">
				<div className="bar">
					<HighlightBar />
				</div>
				<Title dangerouslySetInnerHTML={{ __html: title }} />
				<Subtitle dangerouslySetInnerHTML={{ __html: description }} />
				{isMobile ? (
					<SwiperWrapper>
						<Swiper
							spaceBetween={40}
							slidesPerView={1}
							navigation={false}
							onSlideChange={(e) => setActiveBullet(e.activeIndex)}
						>
							{cards.map((card) => (
								<SwiperSlide key={card.number}>
									<Card>
										<CardNumber>{card.number}</CardNumber>
										<CardTitle>{card.title}</CardTitle>
										<CardText dangerouslySetInnerHTML={{ __html: card.text }} />
									</Card>
								</SwiperSlide>
							))}
							<SlideBulletNavigation activeBullet={activeBullet} />
						</Swiper>
					</SwiperWrapper>
				) : (
					<CardsWrapper>
						{cards.map((card) => (
							<Card key={card.number}>
								<CardNumber>{card.number}</CardNumber>
								<CardTitle>{card.title}</CardTitle>
								<CardText dangerouslySetInnerHTML={{ __html: card.text }} />
							</Card>
						))}
					</CardsWrapper>
				)}
			</div>
		</Section>
	);
}
